import { Box, Typography } from "@mui/material";
import StyledChart, {
  apexChartsTooltipStyles,
} from "../../../../../common/components/apexchart/StyledChart";

import AllLocationsEventsFilterDropdownContainer from "../../../../../common/components/locations/recent-events/AllLocationsEventsFilterDropdownContainer";
import { AutocompleteOptionType } from "../../../../../common/models/autocomplete";
import { CHART_CONTAINER_HEIGHT } from "../../system-health/common/values";
import CircularLoading from "../../../../../common/components/progress/CircularLoading";
import SimplePaperWrapper from "../../../../../common/components/item/SimplePaperWrapper";
import StatisticsNoDataView from "./StatisticsNoDataView";
import { TimePeriod } from "../../../../../API";
import TimePeriodFilter from "./TimePeriodFilter";
import isEqual from "lodash/isEqual";
import { useAllAlertsChart } from "../hooks/useAllAlertsChart";
import { useLocationsFilterDropdown } from "../hooks/useLocationsFilterDropdown";
import { useState } from "react";

const AllAlertsContainer = (): JSX.Element => {
  const [activeTimePeriod, setActiveTimePeriod] = useState<TimePeriod>(
    TimePeriod.ONE_WEEK
  );

  const {
    location,
    handleLocationChange,
    tagLocations,
    handleTagLocationsChange,
  } = useLocationsFilterDropdown();

  const handleTimePeriodChange = (timePeriod: TimePeriod): void => {
    if (timePeriod !== activeTimePeriod) {
      setActiveTimePeriod(timePeriod);
    }
  };

  const { showNoData, series, options, loading, refetching, refetchChartData } =
    useAllAlertsChart(
      activeTimePeriod,
      location?.value
        ? [location?.value]
        : tagLocations?.map(l => l?.value ?? "") ?? null
    );

  const setLocationFilter = (locationValue: AutocompleteOptionType): void => {
    if (locationValue?.value !== location?.value) {
      handleLocationChange(locationValue, activeTimePeriod, refetchChartData);
    }
  };

  const setTagFilter = (locations: AutocompleteOptionType[] | null): void => {
    if (!isEqual(locations, tagLocations)) {
      handleTagLocationsChange(locations, activeTimePeriod, refetchChartData);
    }
  };

  return (
    <SimplePaperWrapper
      sx={{
        padding: "1.5em",
        height: CHART_CONTAINER_HEIGHT,
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Typography variant="h5">All alerts</Typography>
        <Box
          sx={{
            display: "flex",
            gap: "1em",
            marginLeft: "auto",
          }}
        >
          <Box
            sx={{
              width: "180px",
            }}
          >
            <AllLocationsEventsFilterDropdownContainer
              setLocation={setLocationFilter}
              setTagLocations={setTagFilter}
            />
          </Box>
          <TimePeriodFilter
            activePeriod={activeTimePeriod}
            setActivePeriod={handleTimePeriodChange}
          />
        </Box>
      </Box>
      {(loading || refetching) && <CircularLoading />}
      {!loading && !showNoData && (
        <Box sx={apexChartsTooltipStyles}>
          <StyledChart options={options} series={series} height={320} />
        </Box>
      )}
      {showNoData && <StatisticsNoDataView />}
    </SimplePaperWrapper>
  );
};

export default AllAlertsContainer;
